import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Loader2,
  AlertCircle,
  FileText,
  Pencil,
  Trash2,
  LayoutGrid,
  Eye,
  Clock,
} from 'lucide-react';
import { formsApi, appsApi, type Form } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

// Helper to parse form layout and get stats
function getFormStats(form: Form): { sections: number; fields: number; themeColor: string } {
  try {
    const layout = typeof form.layout === 'string' ? JSON.parse(form.layout) : form.layout;
    const sections = layout?.sections || [];
    const fields = sections.reduce((acc: number, s: { fields?: unknown[] }) => acc + (s.fields?.length || 0), 0);
    const themeColor = layout?.theme?.primaryColor || '#3B82F6';
    return { sections: sections.length, fields, themeColor };
  } catch {
    return { sections: 0, fields: 0, themeColor: '#3B82F6' };
  }
}

export function FormsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Fetch forms from API
  const {
    data: formsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['forms'],
    queryFn: () => formsApi.list(),
  });

  // Fetch apps for the mapping
  const { data: appsData } = useQuery({
    queryKey: ['apps'],
    queryFn: () => appsApi.list(),
  });

  // Delete form mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => formsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forms'] });
      setDeleteId(null);
    },
  });

  const handleEdit = (formId: string) => {
    navigate(`/forms/${formId}`);
  };

  const forms = formsData?.items || [];
  const apps = appsData?.items || [];
  const appMap = new Map(apps.map((app) => [app.id, app.name]));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">Formulaires</h2>
          <p className="text-sm text-secondary-500">Créez et gérez vos formulaires</p>
        </div>
        <Button onClick={() => navigate('/forms/new')}>
          <Plus className="mr-2 h-4 w-4" />
          Nouveau Formulaire
        </Button>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
          <span className="ml-2 text-secondary-500">Chargement des formulaires...</span>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
          <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
            <AlertCircle className="h-5 w-5" />
            <span>Erreur lors du chargement: {(error as Error).message}</span>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && forms.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-secondary-300 py-12 dark:border-secondary-700">
          <FileText className="h-12 w-12 text-secondary-400" />
          <h3 className="mt-4 text-lg font-medium text-secondary-900 dark:text-white">
            Aucun formulaire
          </h3>
          <p className="mt-2 text-sm text-secondary-500">
            Créez votre premier formulaire pour collecter des données.
          </p>
          <Button className="mt-4" onClick={() => navigate('/forms/new')}>
            <Plus className="mr-2 h-4 w-4" />
            Créer un formulaire
          </Button>
        </div>
      )}

      {/* Forms grid */}
      {!isLoading && !error && forms.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {forms.map((form) => {
            const stats = getFormStats(form);

            return (
              <div
                key={form.id}
                className="group relative rounded-xl border border-secondary-200 bg-white overflow-hidden transition-all hover:shadow-lg hover:border-secondary-300 dark:border-secondary-800 dark:bg-secondary-900 dark:hover:border-secondary-700"
              >
                {/* Color accent bar */}
                <div
                  className="h-1.5 w-full"
                  style={{ backgroundColor: stats.themeColor }}
                />

                <div className="p-5">
                  {/* Header */}
                  <div className="flex items-start gap-3 mb-3">
                    <div
                      className="flex h-12 w-12 items-center justify-center rounded-xl flex-shrink-0"
                      style={{ backgroundColor: `${stats.themeColor}15` }}
                    >
                      <FileText
                        className="h-6 w-6"
                        style={{ color: stats.themeColor }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-secondary-900 dark:text-white truncate">
                        {form.name}
                      </h3>
                      <p className="text-sm text-secondary-500 truncate">
                        {appMap.get(form.app_id) || 'Application'}
                      </p>
                    </div>
                  </div>

                  {/* Description */}
                  {form.description ? (
                    <p className="text-sm text-secondary-600 dark:text-secondary-400 line-clamp-2 mb-4">
                      {form.description}
                    </p>
                  ) : (
                    <p className="text-sm text-secondary-400 dark:text-secondary-500 italic mb-4">
                      Aucune description
                    </p>
                  )}

                  {/* Stats badges */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Badge variant="secondary" className="text-xs">
                      <LayoutGrid className="h-3 w-3 mr-1" />
                      {stats.sections} section{stats.sections > 1 ? 's' : ''}
                    </Badge>
                    <Badge variant="secondary" className="text-xs">
                      <FileText className="h-3 w-3 mr-1" />
                      {stats.fields} champ{stats.fields > 1 ? 's' : ''}
                    </Badge>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-3 border-t border-secondary-100 dark:border-secondary-800">
                    <div className="flex items-center gap-1 text-xs text-secondary-400">
                      <Clock className="h-3 w-3" />
                      {new Date(form.created_at).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleEdit(form.id)}
                        title="Modifier"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleEdit(form.id)}
                        title="Prévisualiser"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Dialog open={deleteId === form.id} onOpenChange={(open) => !open && setDeleteId(null)}>
                        <DialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                            onClick={() => setDeleteId(form.id)}
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Supprimer le formulaire</DialogTitle>
                            <DialogDescription>
                              Êtes-vous sûr de vouloir supprimer "{form.name}" ? Cette action est irréversible.
                            </DialogDescription>
                          </DialogHeader>
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setDeleteId(null)}>
                              Annuler
                            </Button>
                            <Button
                              variant="destructive"
                              onClick={() => deleteMutation.mutate(form.id)}
                              disabled={deleteMutation.isPending}
                            >
                              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                              Supprimer
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
